export type OrgPersonTextAlignment = 'left' | 'center' | 'right';

export type OrgPersonPhotoVariant = 'top_center' | 'left' | 'right';

export type OrgPersonCardViewMode = 'compact' | 'full';

export const ORG_PERSON_CARD_SPECS = {
  compact: {
    card: { width: 160, height: 72 },
    cardTopCenter: { width: 160, height: 73 },
    cardInnerLeft: { width: 180, height: 72 },
    image: {
      top_center: { size: 72 },
      left: { size: 72 },
      right: { size: 72 },
    },
  },
  full: {
    card: {
      text: { width: 300, height: 180 },
      image: { width: 300, height: 206 },
      innerLeft: { width: 300, height: 210 },
    },
    image: {
      top_center: { size: 72 },
      left: { size: 72 },
      right: { size: 72 },
    },
  },
} as const;

export function resolveOrgPersonCardDimensions(
  nodeType: string,
  viewMode: OrgPersonCardViewMode,
  showFooter = false,
): { width: number; height: number } {
  let dimensions: { readonly width: number; readonly height: number };
  if (viewMode === 'compact') {
    if (nodeType === 'org_person_photo') dimensions = ORG_PERSON_CARD_SPECS.compact.cardTopCenter;
    else if (nodeType === 'org_person_photo_inner_left') dimensions = ORG_PERSON_CARD_SPECS.compact.cardInnerLeft;
    else dimensions = ORG_PERSON_CARD_SPECS.compact.card;
  } else if (nodeType === 'org_person_text') {
    dimensions = ORG_PERSON_CARD_SPECS.full.card.text;
  } else if (nodeType === 'org_person_photo_inner_left') {
    dimensions = ORG_PERSON_CARD_SPECS.full.card.innerLeft;
  } else {
    dimensions = ORG_PERSON_CARD_SPECS.full.card.image;
  }

  const footerHeight = !showFooter ? 0 : nodeType === 'org_person_text' ? 22 : viewMode === 'compact' ? 26 : 28;
  return { width: dimensions.width, height: dimensions.height + footerHeight };
}

export function resolveOrgPersonRenderedCardHeight(
  nodeType: string,
  persistedHeight: number,
  showFooter: boolean,
): number {
  if (!showFooter || !Number.isFinite(persistedHeight) || persistedHeight <= 0) {
    return persistedHeight;
  }

  const compactDimensions = resolveOrgPersonCardDimensions(nodeType, 'compact', false);
  const fullDimensions = resolveOrgPersonCardDimensions(nodeType, 'full', false);
  const compactWithFooter = resolveOrgPersonCardDimensions(nodeType, 'compact', true);
  const fullWithFooter = resolveOrgPersonCardDimensions(nodeType, 'full', true);
  const matches = (value: number) => Math.abs(persistedHeight - value) <= 1;

  if (matches(compactWithFooter.height) || matches(fullWithFooter.height)) {
    return persistedHeight;
  }
  if (matches(compactDimensions.height)) return compactWithFooter.height;
  if (matches(fullDimensions.height)) return fullWithFooter.height;

  return persistedHeight;
}

export function resolveOrgShapeDefaultDimensions(
  nodeType: string,
  viewMode: OrgPersonCardViewMode,
): { width: number; height: number } | null {
  if (
    nodeType === 'org_person_photo' ||
    nodeType === 'org_person_photo_left' ||
    nodeType === 'org_person_photo_right' ||
    nodeType === 'org_person_photo_inner_left' ||
    nodeType === 'org_person_text'
  ) {
    return resolveOrgPersonCardDimensions(nodeType, viewMode);
  }

  if (nodeType === 'org_position') return { width: 160, height: 72 };
  if (nodeType === 'org_department') return { width: 270, height: 60 };
  if (nodeType === 'org_person') return { width: 270, height: 80 };

  return null;
}

export const ORGCHART_SHADOWS_ENABLED = false;
